// src/services/invitationService.ts
import { post, get } from 'aws-amplify/api';
import { TripRole } from '../API';

// Request/Response Types
export interface CreateTripInvitationRequest {
  tripId: string;
  role: TripRole;
}

export interface TripInvitationResponse {
  id: string;
  link: string;
  role: TripRole;
  expiresAt: number;
}

export interface TripWithInvitationInfo {
  id: string;
  tripName: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  createdBy: string;
  invitationToken: string;
  invitationRole: TripRole;
}

export interface TripMemberResponse {
  id: string;
  tripId: string;
  userId: string;
  role: TripRole;
  createdAt: string;
  updatedAt: string;
}

// Error handling helper
const handleApiResponse = async (response: any) => {
  try {
    // Check if response has a body that can be parsed
    if (response.body) {
      const responseBody = await response.body.json();
      
      // Check if response contains an error
      if (response.statusCode >= 400 || responseBody.error) {
        const errorMessage = responseBody.message || responseBody.error || 'An error occurred';
        throw new Error(errorMessage);
      }
      
      return responseBody;
    } 
    
    return response;
  } catch (error) {
    // If error occurred during parsing, throw a more specific error
    if (error instanceof SyntaxError) {
      throw new Error('Invalid response from server');
    }
    throw error;
  }
};

// Using direct REST API calls
export const createTripInvitation = async (
  tripId: string, 
  role: TripRole
): Promise<TripInvitationResponse> => {
  try {
    const response = await post({
      apiName: 'invitationAPI',
      path: '/invitations',
      options: {
        body: { tripId, role }
      }
    });
    const handledResponse = await handleApiResponse(await response.response);
    return handledResponse as unknown as TripInvitationResponse;;
  } catch (error: any) {
    console.error('Error creating trip invitation:', error);
    throw error;
  }
};

export const validateTripInvitation = async (
  token: string
): Promise<TripWithInvitationInfo> => {
  try {
    const response = await get({
      apiName: 'invitationAPI',
      path: '/invitations/validate',
      options: {
        queryParams: { token }
      }
    });
    const handledResponse = await handleApiResponse(await response.response);

    return handledResponse as unknown as TripWithInvitationInfo;
  } catch (error: any) {
    console.error('Error validating trip invitation:', error);
    
    // Check for specific error messages
    if (error.message && (
      error.message.includes('already been used') || 
      error.message.includes('expired') ||
      error.message.includes('invalid')
    )) {
      throw error;
    }
    
    // If it's another type of error, provide a more descriptive message
    throw new Error('This invitation link is invalid or has already been used.');
  }
};

